import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAnalyticsStats } from '../hooks/useAnalyticsStats';
import { useAggregation } from '../hooks/useAggregation';
import { presetToDateRange, getDefaultCustomRange } from '../utils/datePresets';
import {
  MetricCards,
  DailyTrendsChart,
  DeviceBreakdownChart,
  TopContentTable,
  RetentionTable,
  AggregationPanel,
} from '../components/analytics';
import type { DatePreset } from '../types/analytics';

export default function AnalyticsDashboardPage() {
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>('this_week');
  const [customDateFrom, setCustomDateFrom] = useState(() => getDefaultCustomRange().dateFrom);
  const [customDateTo, setCustomDateTo] = useState(() => getDefaultCustomRange().dateTo);

  const dateRange = useMemo(() => {
    return presetToDateRange({
      preset: selectedPreset,
      dateFrom: customDateFrom,
      dateTo: customDateTo,
    });
  }, [selectedPreset, customDateFrom, customDateTo]);

  const { data, isLoading } = useAnalyticsStats(dateRange);
  const aggregation = useAggregation();

  const handleCustomDateChange = (dateFrom: string, dateTo: string) => {
    setCustomDateFrom(dateFrom);
    setCustomDateTo(dateTo);
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <h1 className="text-heading-2 text-admin-slate-400 mb-1">Analytics</h1>
        <p className="text-body-sm text-admin-slate-500">User engagement and content performance metrics.</p>
      </motion.div>

      <AggregationPanel
        selectedPreset={selectedPreset}
        onPresetChange={setSelectedPreset}
        customDateFrom={customDateFrom}
        customDateTo={customDateTo}
        onCustomDateChange={handleCustomDateChange}
        onTriggerAggregation={aggregation.trigger}
        isRunning={aggregation.isRunning}
        isPending={aggregation.isPending}
        lastRun={data?.lastAggregationRun ?? null}
      />

      {data?.summary && <MetricCards summary={data.summary} isLoading={isLoading} />}
      {isLoading && !data && <MetricCards summary={{} as Parameters<typeof MetricCards>[0]['summary']} isLoading={true} />}

      {data && (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <DailyTrendsChart
              dailyTotals={data.dailyTotals}
              title="Users & Sessions"
              syncId="analytics"
              metrics={[
                { key: 'active_users', label: 'Active Users', color: '#3b82f6' },
                { key: 'unique_visitors', label: 'Visitors', color: '#10b981' },
                { key: 'sessions', label: 'Sessions', color: '#8b5cf6' },
              ]}
            />
            <DailyTrendsChart
              dailyTotals={data.dailyTotals}
              title="Content Engagement"
              syncId="analytics"
              metrics={[
                { key: 'activity_views', label: 'Activity Views', color: '#f59e0b' },
                { key: 'activity_completions', label: 'Completions', color: '#10b981' },
                { key: 'activities_saved', label: 'Saved', color: '#3b82f6' },
                { key: 'expert_tips_viewed', label: 'Tips Viewed', color: '#8b5cf6' },
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <DeviceBreakdownChart devices={data.devices} dimension="device_type" title="Device Types" />
            <DeviceBreakdownChart devices={data.devices} dimension="browser" title="Browsers" />
            <DeviceBreakdownChart devices={data.devices} dimension="os" title="Operating Systems" />
          </div>

          <RetentionTable cohorts={data.retention.cohorts} latest={data.retention.latest} />

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <TopContentTable content={data.topContent} contentType="activity" title="Top 10 Activities" />
            <TopContentTable content={data.topContent} contentType="expert_tip" title="Top 10 Expert Tips" />
          </div>
        </>
      )}
    </div>
  );
}
